import { DisplayPaynow, PaynowSettings, QrStyle } from '../../../api/client';

/** How a PayNow destination is identified: a handphone or a business UEN. */
export type Kind = 'mobile' | 'uen';

/** The editable PayNow settings; exactly the six fields the form owns. */
export type PaynowForm = {
  kind: Kind;
  value: string;
  name: string;
  color: string;
  style: QrStyle;
  logoId: number | null;
};

/** The current branding image, as the form shows it. */
export type LogoDisplay = { url: string | null; name: string | null };

/** The live preview's lifecycle. */
export type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; preview: DisplayPaynow; url: string }
  | { status: 'unavailable' }
  | { status: 'error' };

/** The preview has no basket, so it draws a fixed stand-in total. */
export const PREVIEW_AMOUNT_CENTS = 1234;

/** A blank form, before the account's settings have arrived. */
export const EMPTY_PAYNOW_FORM: PaynowForm = {
  kind: 'mobile',
  value: '',
  name: '',
  color: '#000000',
  style: 'plain',
  logoId: null,
};

/** The setter every field shares. */
export type SetPaynowField = <K extends keyof PaynowForm>(key: K, value: PaynowForm[K]) => void;

/** Narrow a stored or selected type to the two the form understands. */
export function parseKind(value: string): Kind {
  return value === 'uen' ? 'uen' : 'mobile';
}

const QR_STYLES = ['plain', 'logo', 'halftone', 'photo'] as const;

/** Narrow a stored or selected style, falling back to the plain code. */
export function parseQrStyle(value: string): QrStyle {
  return QR_STYLES.find((style) => style === value) ?? 'plain';
}

/**
 * Compare the six editable fields explicitly.
 *
 * A serialised key was a stand-in for this: it hid which field changed and
 * would silently accept a shape change.
 */
export function isSamePaynowForm(a: PaynowForm, b: PaynowForm): boolean {
  return (
    a.kind === b.kind && a.value === b.value && a.name === b.name && a.color === b.color && a.style === b.style && a.logoId === b.logoId
  );
}

/** The API's settings in the form's shape. */
export function formFromSettings(settings: PaynowSettings): PaynowForm {
  return {
    kind: parseKind(settings.type ?? ''),
    value: settings.value ?? '',
    name: settings.name ?? '',
    color: settings.color ?? '#000000',
    style: settings.style ?? (settings.logo_url ? 'logo' : 'plain'),
    logoId: settings.logo_id,
  };
}
